import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { useAdmin } from '../contexts/AdminContext'

const CSS_HANDLES = [
  'adminLayout',
  'adminSidebar',
  'adminSidebarContainer',
  'adminSidebarHeader',
  'adminLogo',
  'adminLogoImage',
  'adminLogoText',
  'adminNav',
  'adminNavList',
  'adminNavItem',
  'adminNavLink',
  'adminNavLinkActive',
  'adminNavIconWrapper',
  'adminNavIcon',
  'adminNavLabel',
  'adminNavBadge',
  'adminNavBadge',
  'adminMobileBar',
  'adminMobileMenuButton',
  'adminMobileLogo',
  'adminMain',
  'adminContent',
  'adminContentInner'
] as const

interface AdminLayoutProps {
  children: React.ReactNode
}

type NavLinkItem = {
  to: string
  label: string
  icon: (className: string) => React.ReactNode
  badge?: () => React.ReactNode | number | null
  children?: NavLinkItem[]
}

const NAV_LINKS: NavLinkItem[] = [
  {
    to: '/admin',
    label: 'Dashboard',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0h6" />
      </svg>
    )
  },
  {
    to: '/admin/cadastros',
    label: 'Cadastros',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    children: [
      {
        to: '/admin/cadastros/marcas',
        label: 'Marcas',
        icon: (className) => (
          <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        )
      },
      {
        to: '/admin/cadastros/categorias',
        label: 'Categorias',
        icon: (className) => (
          <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
        )
      },
      {
        to: '/admin/cadastros/zonas-entrega',
        label: 'Zonas de Entrega',
        icon: (className) => (
          <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        )
      }
    ]
  },
  {
    to: '/admin/produtos',
    label: 'Produtos',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V7a2 2 0 00-2-2h-5V3H7a2 2 0 00-2 2v6m15 7V11a2 2 0 00-2-2h-5m-4 11h10a2 2 0 002-2M3 17a2 2 0 002 2h6V9H5a2 2 0 00-2 2v6z" />
      </svg>
    )
  },
  {
    to: '/admin/pedidos',
    label: 'Pedidos',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h2l.4 2m0 0H20l-1.34 5.34a2 2 0 01-1.94 1.49H8.28a2 2 0 01-1.94-1.49L5 9m.4 0L4 4H2" />
        <circle cx="9" cy="20" r="1" />
        <circle cx="17" cy="20" r="1" />
      </svg>
    ),
    badge: () => null
  },
  {
    to: '/admin/relatorios',
    label: 'Relatórios',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 19h16M4 15h16M10 11h10M4 7h10" />
      </svg>
    )
  },
  {
    to: '/admin/cupons',
    label: 'Cupons',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A2 2 0 013 15.382V8.618a2 2 0 011.553-1.894L9 4m6 16l5.447-2.724A2 2 0 0021 15.382V8.618a2 2 0 00-1.553-1.894L15 4m0 16V4m-6 16V4" />
      </svg>
    )
  },
  {
    to: '/admin/clientes',
    label: 'Clientes',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-5-4m0 6v-2a4 4 0 00-4-4H9a4 4 0 00-4 4v2h12zm-2-14a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )
  },
  {
    to: '/admin/reviews',
    label: 'Avaliações',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.122 3.455a1 1 0 00.95.69h3.631c.969 0 1.371 1.24.588 1.81l-2.94 2.138a1 1 0 00-.364 1.118l1.123 3.455c.3.921-.755 1.688-1.538 1.118l-2.94-2.138a1 1 0 00-1.176 0l-2.94 2.138c-.783.57-1.838-.197-1.539-1.118l1.123-3.455a1 1 0 00-.364-1.118l-2.94-2.138c-.783-.57-.38-1.81.588-1.81h3.632a1 1 0 00.949-.69l1.123-3.455z" />
      </svg>
    )
  },
  {
    to: '/admin/comunicacao',
    label: 'Comunicação',
    icon: (className) => (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    children: [
      {
        to: '/admin/comunicacao/templates-email',
        label: 'Templates de Email',
        icon: (className) => (
          <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        )
      }
    ]
  }
]

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const handles = useCssHandles(CSS_HANDLES)
  const location = useLocation()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())
  const { pendingOrdersCount } = useAdmin()

  const isActiveLink = (to: string) => {
    if (location.pathname === to) {
      return true
    }

    if (to !== '/admin' && location.pathname.startsWith(`${to}/`)) {
      return true
    }

    return false
  }

  const hasActiveChild = (item: NavLinkItem): boolean => {
    if (!item.children) return false
    return item.children.some(child => isActiveLink(child.to))
  }

  const toggleMenu = (to: string) => {
    setExpandedMenus(prev => {
      const next = new Set(prev)
      if (next.has(to)) {
        next.delete(to)
      } else {
        next.add(to)
      }
      return next
    })
  }

  const SidebarContent: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
    <div className={`${handles.adminSidebarContainer} flex h-full flex-col`}>
      <div className={`${handles.adminSidebarHeader} flex h-20 items-center justify-center border-b border-gray-200 px-6`}>
        <Link
          to="/admin"
          className={`${handles.adminLogo} flex items-center space-x-3`}
          onClick={onNavigate}
        >
          <img
            src="/assets/logo-cor-sem-slogan.svg"
            alt="BaterPower"
            className={`${handles.adminLogoImage} h-10 w-auto`}
          />
        </Link>
      </div>

      <nav className={`${handles.adminNav} flex-1 overflow-y-auto px-3 py-6`}>
        <ul className={`${handles.adminNavList} space-y-1`}>
          {NAV_LINKS.map((link) => {
            const active = isActiveLink(link.to) || hasActiveChild(link)
            const hasChildren = link.children && link.children.length > 0
            const isExpanded = expandedMenus.has(link.to) || hasActiveChild(link)
            
            const linkClasses = [
              handles.adminNavLink,
              'group flex items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-150',
              active
                ? `${handles.adminNavLinkActive} bg-blue-50 text-blue-600`
                : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2'
            ].join(' ')

            return (
              <li key={link.to} className={`${handles.adminNavItem}`}>
                {hasChildren ? (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleMenu(link.to)}
                      className={linkClasses + ' w-full'}
                    >
                      <span
                        className={`${handles.adminNavIconWrapper} mr-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100/70 transition-colors duration-150 group-hover:bg-blue-100`}
                        aria-hidden="true"
                      >
                        {link.icon(
                          `${handles.adminNavIcon} h-5 w-5 ${active ? 'text-blue-600' : 'text-blue-500'}`
                        )}
                      </span>
                      <span className={`${handles.adminNavLabel} flex items-center gap-2 flex-1`}>
                        <span>{link.label}</span>
                        <svg
                          className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </span>
                    </button>
                    {isExpanded && hasChildren && link.children && (
                      <ul className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-4">
                        {link.children.map((child) => {
                          const childActive = isActiveLink(child.to)
                          const childLinkClasses = [
                            handles.adminNavLink,
                            'group flex items-center rounded-xl px-3 py-2 text-sm font-medium transition-colors duration-150',
                            childActive
                              ? `${handles.adminNavLinkActive} bg-blue-50 text-blue-600`
                              : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                          ].join(' ')

                          return (
                            <li key={child.to}>
                              <Link
                                to={child.to}
                                className={childLinkClasses}
                                onClick={onNavigate}
                              >
                                <span
                                  className={`${handles.adminNavIconWrapper} mr-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-blue-100/50 transition-colors duration-150 group-hover:bg-blue-100`}
                                  aria-hidden="true"
                                >
                                  {child.icon(
                                    `${handles.adminNavIcon} h-4 w-4 ${childActive ? 'text-blue-600' : 'text-blue-500'}`
                                  )}
                                </span>
                                <span className={`${handles.adminNavLabel}`}>
                                  {child.label}
                                </span>
                              </Link>
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={link.to}
                    className={linkClasses}
                    onClick={onNavigate}
                  >
                    <span
                      className={`${handles.adminNavIconWrapper} mr-3 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100/70 transition-colors duration-150 group-hover:bg-blue-100`}
                      aria-hidden="true"
                    >
                      {link.icon(
                        `${handles.adminNavIcon} h-5 w-5 ${active ? 'text-blue-600' : 'text-blue-500'}`
                      )}
                    </span>
                    <span className={`${handles.adminNavLabel} flex items-center gap-2`}>
                      <span>{link.label}</span>
                      {link.to === '/admin/pedidos' && pendingOrdersCount !== null && pendingOrdersCount > 0 && (
                        <span className={`${handles.adminNavBadge} inline-flex min-w-[1.75rem] items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white`}>
                          {pendingOrdersCount > 99 ? '99+' : pendingOrdersCount}
                        </span>
                      )}
                    </span>
                  </Link>
                )}
              </li>
            )
          })}
        </ul>
      </nav>

      <div className="border-t border-gray-200 px-6 py-5">
        <Link
          to="/"
          className="block rounded-lg border border-gray-200 px-4 py-3 text-center text-sm font-medium text-gray-700 transition-colors duration-150 hover:border-blue-600 hover:text-blue-600"
        >
          Voltar para a loja
        </Link>
      </div>
    </div>
  )

  return (
    <div className={`${handles.adminLayout} min-h-screen bg-gray-100`}>
      {isSidebarOpen && (
        <>
          <button
            type="button"
            aria-label="Fechar menu"
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
          <aside className={`${handles.adminSidebar} fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl md:hidden`}>
            <SidebarContent onNavigate={() => setIsSidebarOpen(false)} />
          </aside>
        </>
      )}

      <aside className={`${handles.adminSidebar} hidden md:fixed md:inset-y-0 md:left-0 md:z-30 md:flex md:w-72 md:flex-col md:border-r md:border-gray-200 md:bg-white`}>
        <SidebarContent />
      </aside>

      <div className={`${handles.adminMain} flex min-h-screen flex-1 flex-col md:pl-72`}>
        <header className={`${handles.adminMobileBar} sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white px-4 py-4 md:hidden`}>
          <button
            type="button"
            className={`${handles.adminMobileMenuButton} inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:border-blue-600 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
            onClick={() => setIsSidebarOpen(true)}
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Menu
          </button>

          <Link to="/admin" className={`${handles.adminMobileLogo} flex items-center space-x-2`}>
            <img src="/assets/logo-cor-com-slogan.svg" alt="BaterPower" className="h-8 w-auto" />
            <span className="text-base font-semibold text-gray-900">Painel</span>
          </Link>
        </header>

        <main className={`${handles.adminContent} w-full flex-1 bg-gray-50`}>
          <div className={`${handles.adminContentInner} w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-10`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout

